'use strict';

const { h, Component } = require('phy');
const {
  isTraitAvailable,
  isPartOfCurrentSelection,
} = require('../util/trait_available');
const TraitsTitle = require('./TraitsTitle');
const TraitsError = require('./TraitsError');

const formatTextForBloodhound = text =>
  `${text
    .toLowerCase()
    .split(' ')
    .join('-')}`;

const getTraitBhwTag = traitText =>
  `VariationsTrait:${formatTextForBloodhound(traitText)}`;

const getOptionBhcTag = optionText =>
  `option:${formatTextForBloodhound(optionText)}`;

const reconstructTraitOptionsMap = (dealOptions, traitOptionsMap) => {
  const traitsOptions = {};
  Object.keys(traitOptionsMap).forEach(key => {
    traitsOptions[key] = traitOptionsMap[key].map(optionUuid =>
      dealOptions.find(option => option.uuid === optionUuid)
    );
  });
  return traitsOptions;
};

const getNotEmptySelectedTraits = (traits, ignoredKey) =>
  Object.keys(traits).reduce((notEmptyTraits, key) => {
    if (key !== ignoredKey && traits[key]) {
      notEmptyTraits[key] = traits[key]; // eslint-disable-line no-param-reassign
    }
    return notEmptyTraits;
  }, {});

const selectedValueExistsInOptionTraits = (targetTrait, optionsTraits = []) =>
  optionsTraits.find(
    currentOptionTrait =>
      currentOptionTrait.name === targetTrait.name &&
      currentOptionTrait.value === targetTrait.value
  );

// make sure to only pass selectedTraits with a value otherwise it will not find the correct option
const getCheapestOptionFromSelectedTraits = (
  selectedTraits,
  options,
  traitOptionsMap
) => {
  const selectedTraitsKeys = Object.keys(selectedTraits);
  let optionFound;
  options.forEach(option => {
    const optionHasSelectedTraits = selectedTraitsKeys.every(selectedKey =>
      selectedValueExistsInOptionTraits(
        { name: selectedKey, value: selectedTraits[selectedKey] },
        option.traits
      )
    );
    const isSoldOut = option.isSoldOut;
    const isUnavailable = option.status !== 'open';
    if (optionHasSelectedTraits && !(isSoldOut || isUnavailable)) {
      if (
        !optionFound ||
        (optionFound && optionFound.price.amount > option.price.amount)
      ) {
        optionFound = option;
      }
    }
  });
  if (optionFound) {
    return optionFound;
  }
  // If options doesn't have option.traits try to find it using traitOptionsMap
  if (selectedTraitsKeys.length === 1) {
    const keyValue = `${selectedTraitsKeys[0]}-${
      selectedTraits[selectedTraitsKeys[0]]
    }`;
    if (traitOptionsMap[keyValue] && traitOptionsMap[keyValue].length) {
      return traitOptionsMap[keyValue][0];
    }
  }
  // default return first option
  return options[0];
};

const addGetawaysFromOptionPrice = (
  pageType,
  isSelected,
  optionText,
  name,
  value,
  selectedTraits,
  dealOptions,
  traitsOptions,
  I18n
) => {
  if (pageType === 'getaways' && !isSelected) {
    const totalTraits = Object.keys(selectedTraits).length;
    const simulatedSelectedTraits = getNotEmptySelectedTraits(selectedTraits);
    const selectionsRemaining =
      totalTraits - Object.keys(simulatedSelectedTraits).length;
    simulatedSelectedTraits[name] = value;
    const cheapestOption = getCheapestOptionFromSelectedTraits(
      simulatedSelectedTraits,
      dealOptions,
      traitsOptions
    );
    if (
      totalTraits === 1 ||
      selectionsRemaining === 0 ||
      (selectionsRemaining === 1 && !selectedTraits[name])
    ) {
      return `${optionText} (${cheapestOption.price.formattedAmount})`;
    } else {
      const fromText = I18n.t('js.modules.variations.from_price', {
        minPrice: cheapestOption.price.formattedAmount,
      });
      return `${optionText} (${fromText})`;
    }
  }
  return optionText;
};

class Traits extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTraits: this.initTraitsState(),
      showError: false,
      traits: props.traits,
    };
    this.traitsOptions = reconstructTraitOptionsMap(
      props.dealOptions,
      props.traitOptionsMap
    );
    this.showVariationsError = this.showVariationsError.bind(this);
    this.updateFromCarousel = this.updateFromCarousel.bind(this);
  }

  componentDidMount() {
    window.DealEventPipe.on('carouselUpdate', this.updateFromCarousel);
    window.BuyButton.eventBus.board('buy_button_clicked', () => {
      const allTraitsSelected =
        Object.keys(getNotEmptySelectedTraits(this.state.selectedTraits))
          .length === this.state.traits.length;
      if (allTraitsSelected) {
        return {};
      } else {
        this.showVariationsError();
        window.BuyButton.view.enableBuyButton();
        return { stopTheBus: true };
      }
    });
    window.DealEventPipe.on('showVariationsError', this.showVariationsError);
    // if there is a preselected option trigger a simulated first selection
    const { preSelectedTraits } = this.props;
    if (preSelectedTraits) {
      const firstOption = preSelectedTraits[0];
      this.handleTraitSelection({
        trait: firstOption.name,
        option: firstOption.value,
      });
    }
  }

  initTraitsState() {
    const { traits, preSelectedTraits } = this.props;
    const traitsState = {};
    traits.forEach(trait => {
      traitsState[trait.name] = '';
    });
    if (preSelectedTraits) {
      preSelectedTraits.forEach(trait => {
        traitsState[trait.name] = trait.value;
      });
    }
    return traitsState;
  }

  showVariationsError() {
    this.setState({
      ...this.state,
      showError: true,
    });
  }

  findCurrentStateTraitValue(trait, value) {
    const stateTrait = this.state.traits.find(
      currentTrait => currentTrait.name === trait
    );
    return stateTrait.values.find(currentValue => currentValue.value === value);
  }

  updateFromCarousel(carouselData) {
    for (const trait in carouselData) {
      if (carouselData[trait].length === 1) {
        const value = carouselData[trait][0];
        // To avoid selecting a sold out option from the carousel
        let traitValue = this.findCurrentStateTraitValue(trait, value);
        if (traitValue.isAvailable) {
          this.handleTraitSelection({
            trait,
            option: value,
          });
        } else if (this.props.clearTraitsOnCarouselSelection) {
          // if current options are not available reset and select only the ones available
          this.setState({
            ...this.state,
            selectedTraits: this.initTraitsState(),
            traits: this.props.traits,
          });
          traitValue = this.findCurrentStateTraitValue(trait, value);
          if (traitValue.isAvailable) {
            this.handleTraitSelection({
              trait,
              option: value,
            });
          }
        }
      }
    }
  }

  getOptionsIntersectionOfSelectedTraits(notEmptySelectedTraits) {
    return Object.keys(notEmptySelectedTraits).reduce(
      (selectedTraitsOptionsIntersection, selectedTraitKey) => {
        const keyValue = notEmptySelectedTraits[selectedTraitKey];
        const selectedOptions = this.traitsOptions[
          `${selectedTraitKey}-${keyValue}`
        ];
        if (selectedOptions) {
          if (selectedTraitsOptionsIntersection.length) {
            // eslint-disable-next-line no-param-reassign
            selectedTraitsOptionsIntersection = selectedTraitsOptionsIntersection.filter(
              current =>
                selectedOptions.some(option => option.uuid === current.uuid)
            );
          } else {
            // eslint-disable-next-line no-param-reassign
            selectedTraitsOptionsIntersection = selectedOptions;
          }
        }
        return selectedTraitsOptionsIntersection;
      },
      this.props.dealOptions
    );
  }

  filterAvailableTraits(notEmptySelectedTraits, lastTraitSelected) {
    return this.state.traits.map(trait => {
      const { name, values } = trait;
      let traitValues = values;
      if (!notEmptySelectedTraits[name]) {
        const optionsAvailable = this.getOptionsIntersectionOfSelectedTraits(
          notEmptySelectedTraits
        );
        traitValues = values.map(value => ({
          value: value.value,
          options: value.options,
          isAvailable: isTraitAvailable(value.options, optionsAvailable),
          isCurrentSelection: isPartOfCurrentSelection(
            value.options,
            optionsAvailable
          ),
        }));
      } else if (lastTraitSelected !== name) {
        //create option availability based on other trait selections
        const otherTraitSelections = getNotEmptySelectedTraits(
          notEmptySelectedTraits,
          name
        );
        const selectedTraitsOptions = this.getOptionsIntersectionOfSelectedTraits(
          otherTraitSelections
        );
        traitValues = values.map(value => ({
          value: value.value,
          options: value.options,
          isAvailable: isTraitAvailable(value.options, selectedTraitsOptions),
          isCurrentSelection: isPartOfCurrentSelection(
            value.options,
            selectedTraitsOptions
          ),
        }));
      }
      return { name, values: traitValues };
    });
  }

  handleTraitSelection({ trait, option }) {
    const newSelectedTraits = { ...this.state.selectedTraits };
    newSelectedTraits[trait] = option;
    const notEmptySelectedTraits = getNotEmptySelectedTraits(newSelectedTraits);
    const traitsSelected = Object.keys(notEmptySelectedTraits).length;
    const allTraitsSelected = traitsSelected === this.state.traits.length;
    let newAvailableTraits = this.props.traits;
    if (traitsSelected) {
      newAvailableTraits = this.filterAvailableTraits(
        notEmptySelectedTraits,
        trait
      );
    }
    const currentOption = getCheapestOptionFromSelectedTraits(
      notEmptySelectedTraits,
      this.props.dealOptions,
      this.traitsOptions
    );

    const sharedData = this.getCurrentOptionsData(
      currentOption,
      allTraitsSelected
    );

    // stop showing error after all traits are selected
    const showError = this.state.showError ? !allTraitsSelected : false;

    // delivery estimates and goods local supply depend on this global variable for initiation
    window.VariationsWidget.currentOptionInfo = sharedData;

    window.DealEventPipe.trigger('traitsUpdate', {
      selectedTraits: notEmptySelectedTraits,
    });
    window.DealEventPipe.trigger('priceUpdate', sharedData);
    window.DealEventPipe.trigger('goodsOptionUpdate', sharedData);

    this.setState({
      ...this.state,
      selectedTraits: newSelectedTraits,
      showError,
      traits: newAvailableTraits,
      lastSelectedTrait: trait,
    });
  }

  getCurrentOptionsData(currentOption, allTraitsSelected) {
    //hide discount if is equal to price
    const value =
      currentOption.value.formattedAmount !==
      currentOption.price.formattedAmount
        ? currentOption.value.formattedAmount
        : null;

    const {
      percent,
      savings,
      buyUrl,
      uuid,
      hideOriginalPriceAndDiscount,
      showDifferenceMessage,
      shippingValue,
      valueSourceMetadata,
      sale,
      isSoldOut,
      externalUrl,
      pitchHtml,
    } = currentOption;

    let url = buyUrl;
    if (externalUrl && externalUrl.length) {
      url = externalUrl;
    } else if (
      window.payload.cartStatus &&
      !window.payload.shouldDisableGoodsCart
    ) {
      url = '#';
    }

    return {
      dealUuid: this.props.dealUuid,
      value,
      percent,
      savings,
      price: currentOption.price.formattedAmount,
      lengthClass: currentOption.price.lengthClass,
      buyUrl: url,
      uuid,
      variationsSelected: allTraitsSelected,
      hideOriginalPriceAndDiscount,
      showDifferenceMessage,
      soldMessage: currentOption.soldMessage && currentOption.soldMessage.title,
      shippingValue,
      valueSourceMetadata,
      sale,
      affirmAmount: currentOption.price.affirmAmount,
      amount: currentOption.price.amount,
      url,
      isSoldOut,
      pitchHtml,
    };
  }

  render() {
    const I18n = this.props.I18n || window.I18n;
    const { traits, selectedTraits, showError } = this.state;
    const {
      pageType,
      traitsErrorMsg,
      traitsOptionsTitle,
      printSoldOut,
    } = this.props;
    return h(
      'div',
      {
        'data-bhw': 'VariationsWidget',
        id: 'variation-selector-widget',
      },
      h(TraitsTitle, {
        pageType,
        title: traitsOptionsTitle,
      }),
      h(TraitsError, { showError, traitsErrorMsg }),
      traits.map((trait, traitIndex) => {
        const { name, values } = trait;
        const selectText = I18n.t('js.modules.variations.select', { name });
        const errorClass =
          showError && selectedTraits[name] === '' ? 'error' : '';
        return h(
          'select',
          {
            key: `trait-${traitIndex}`,
            id: `trait-${traitIndex}`,
            'data-bhw': getTraitBhwTag(name),
            name,
            title: selectText,
            'aria-label': selectText,
            required: true,
            onChange: ({ target }) =>
              this.handleTraitSelection({
                trait: target.name,
                option: target.value,
              }),
            className: `v2-dropdown-input variation-trait ${errorClass}`,
          },
          h(
            'option',
            {
              'data-bhc': getOptionBhcTag(selectText),
              value: '',
              label: selectText,
              key: `trait-${traitIndex}-select`,
              id: `trait-${traitIndex}-select`,
              selected: selectedTraits[name] === '' ? 'selected' : null,
            },
            selectText
          ),
          values.map((element, optionIndex) => {
            const { value, isAvailable, isCurrentSelection } = element;
            const optionText =
              printSoldOut && !isAvailable
                ? `${I18n.t('js.modules.variations.sold_out')} ${value}`
                : value;
            const selected = selectedTraits[name] === value ? 'selected' : null;
            const forceDisplayClass =
              printSoldOut && !isAvailable && isCurrentSelection
                ? 'force-display'
                : '';
            const optionTextWithFromPrice = addGetawaysFromOptionPrice(
              pageType,
              selected,
              optionText,
              name,
              value,
              selectedTraits,
              this.props.dealOptions,
              this.traitsOptions,
              I18n
            );
            return h(
              'option',
              {
                value,
                label: optionTextWithFromPrice,
                key: `trait-${traitIndex}-${optionIndex}`,
                id: `trait-${traitIndex}-${optionIndex}`,
                disabled: !isAvailable,
                className: forceDisplayClass,
                selected,
                'data-bhc': getOptionBhcTag(value),
              },
              optionTextWithFromPrice
            );
          })
        );
      })
    );
  }
}

module.exports = Traits;
